// src/components/profile/ChangePassword.tsx
import React, { useState } from 'react';
import { authAPI } from '../../api';
import { Button } from '../ui/Button';
import { Input } from '../ui/input';
import { Eye, EyeOff, Lock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

export const ChangePassword: React.FC = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Password visibility states
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  
  // Password strength
  const [passwordStrength, setPasswordStrength] = useState<'weak' | 'medium' | 'strong'>('weak');
  const [passwordChecks, setPasswordChecks] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });

  const checkPasswordStrength = (pwd: string) => {
    const checks = {
      length: pwd.length >= 8,
      uppercase: /[A-Z]/.test(pwd),
      lowercase: /[a-z]/.test(pwd),
      number: /[0-9]/.test(pwd),
      special: /[!@#$%^&*]/.test(pwd)
    };
    setPasswordChecks(checks);

    const strengthCount = Object.values(checks).filter(Boolean).length;
    if (strengthCount >= 5) setPasswordStrength('strong');
    else if (strengthCount >= 3) setPasswordStrength('medium');
    else setPasswordStrength('weak');
  };

  const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const pwd = e.target.value;
    setNewPassword(pwd);
    checkPasswordStrength(pwd);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('All fields are required');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (passwordStrength === 'weak') {
      if (!window.confirm('This password is weak. Continue anyway?')) {
        return;
      }
    }

    setLoading(true);
    const toastId = toast.loading('Changing password...');

    try {
      const res = await authAPI.changePassword({ 
        currentPassword, 
        newPassword 
      });
      
      if (res.data.success) {
        toast.success('Password changed successfully!', { id: toastId });
        // Reset form
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setPasswordStrength('weak');
        setPasswordChecks({
          length: false,
          uppercase: false,
          lowercase: false,
          number: false,
          special: false
        });
      } else {
        toast.error(res.data.message || 'Failed to change password', { id: toastId });
      }
    } catch (error: any) {
      console.error('Password change error:', error);
      toast.error(error.response?.data?.message || 'Failed to change password', { id: toastId });
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrengthColor = () => {
    switch (passwordStrength) {
      case 'strong': return 'bg-green-500';
      case 'medium': return 'bg-yellow-500';
      case 'weak': return 'bg-red-500';
      default: return 'bg-gray-300';
    }
  };

  const getPasswordStrengthText = () => {
    switch (passwordStrength) {
      case 'strong': return 'Strong password';
      case 'medium': return 'Medium password';
      case 'weak': return 'Weak password';
      default: return '';
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-md">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <Lock className="h-5 w-5 text-emerald-600" />
        Change Password
      </h3>

      {/* Current Password */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700">
          Current Password
        </label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            type={showCurrent ? 'text' : 'password'}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            placeholder="Enter current password"
            className="pl-10 pr-10"
            required
            disabled={loading}
          />
          <button
            type="button"
            onClick={() => setShowCurrent(!showCurrent)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
            tabIndex={-1}
          >
            {showCurrent ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* New Password */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700">
          New Password
        </label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            type={showNew ? 'text' : 'password'}
            value={newPassword}
            onChange={handleNewPasswordChange}
            placeholder="Enter new password"
            className="pl-10 pr-10"
            required
            disabled={loading}
            minLength={6}
          />
          <button
            type="button"
            onClick={() => setShowNew(!showNew)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
            tabIndex={-1}
          >
            {showNew ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
          </button>
        </div>

        {/* Password strength indicator */}
        {newPassword && (
          <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className={`h-2 w-16 rounded-full ${getPasswordStrengthColor()}`} />
                <span className={`text-xs font-medium ${
                  passwordStrength === 'strong' ? 'text-green-600' :
                  passwordStrength === 'medium' ? 'text-yellow-600' :
                  'text-red-600'
                }`}>
                  {getPasswordStrengthText()}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="flex items-center gap-1">
                {passwordChecks.length ? 
                  <CheckCircle className="h-3 w-3 text-green-500" /> : 
                  <XCircle className="h-3 w-3 text-red-500" />
                }
                <span className={passwordChecks.length ? 'text-green-700' : 'text-red-700'}>
                  8+ characters
                </span>
              </div>
              <div className="flex items-center gap-1">
                {passwordChecks.uppercase ? 
                  <CheckCircle className="h-3 w-3 text-green-500" /> : 
                  <XCircle className="h-3 w-3 text-red-500" />
                }
                <span className={passwordChecks.uppercase ? 'text-green-700' : 'text-red-700'}>
                  Uppercase letter
                </span>
              </div>
              <div className="flex items-center gap-1">
                {passwordChecks.lowercase ? 
                  <CheckCircle className="h-3 w-3 text-green-500" /> : 
                  <XCircle className="h-3 w-3 text-red-500" />
                }
                <span className={passwordChecks.lowercase ? 'text-green-700' : 'text-red-700'}>
                  Lowercase letter
                </span>
              </div>
              <div className="flex items-center gap-1">
                {passwordChecks.number ? 
                  <CheckCircle className="h-3 w-3 text-green-500" /> : 
                  <XCircle className="h-3 w-3 text-red-500" />
                }
                <span className={passwordChecks.number ? 'text-green-700' : 'text-red-700'}>
                  Number
                </span>
              </div>
              <div className="flex items-center gap-1 col-span-2">
                {passwordChecks.special ? 
                  <CheckCircle className="h-3 w-3 text-green-500" /> : 
                  <XCircle className="h-3 w-3 text-red-500" />
                }
                <span className={passwordChecks.special ? 'text-green-700' : 'text-red-700'}>
                  Special character (!@#$%^&*)
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Confirm Password */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700">
          Confirm New Password
        </label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            type={showConfirm ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm new password"
            className="pl-10 pr-10"
            required
            disabled={loading}
          />
          <button
            type="button"
            onClick={() => setShowConfirm(!showConfirm)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
            tabIndex={-1}
          >
            {showConfirm ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
          </button>
        </div>

        {/* Password match indicator */}
        {confirmPassword && newPassword !== confirmPassword && (
          <div className="mt-2 p-2 bg-red-50 rounded-lg border border-red-200">
            <p className="text-xs text-red-600 flex items-center gap-1">
              <XCircle className="h-3 w-3" />
              Passwords do not match
            </p>
          </div>
        )}
        {confirmPassword && newPassword === confirmPassword && newPassword.length > 0 && (
          <div className="mt-2 p-2 bg-green-50 rounded-lg border border-green-200">
            <p className="text-xs text-green-600 flex items-center gap-1">
              <CheckCircle className="h-3 w-3" />
              Passwords match
            </p>
          </div>
        )}
      </div>

      {/* Info box */}
      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-blue-700">
            Choose a strong password with at least 8 characters, including uppercase, lowercase, numbers, and special characters.
          </p>
        </div>
      </div>

      {/* Submit button */}
      <Button
        type="submit"
        disabled={loading || !currentPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword}
        className="w-full bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white py-3"
      >
        {loading ? 'Changing Password...' : 'Change Password'}
      </Button>

      <p className="text-xs text-center text-gray-500">
        You'll be logged out after changing your password
      </p>
    </form>
  );
};